import React, {
  useState,
  useRef,
  memo,
  useCallback,
  useEffect,
} from 'react';
import { Spin } from 'antd';
import ReactPlayer from 'react-player';

import VideoControls from './VideoControls';
import withZoomViewer from '../hoc/withZoomViewer';

interface VideoItemProps {
  src: string | null;
  download?: (src: string, title: string) => void;
  downloadFileName?: string;
  stillFrame?: (camera: string, frameTimestamp: string) => void;
  onAspectRatioChange?: (aspectRatio: number | null) => void;
}

interface VideoPlayerProps {
  playerRef: any;
  src: string;
  isPlaying: boolean;
  loading: boolean;
  activeSrcRef: any;
  isSeeking: boolean;
  setLoading: (value: boolean) => void;
  setIsReady: (value: boolean) => void;
  setPlayedSeconds: (value: number) => void;
  setDuration: (value: number) => void;
  setIsPlaying: (value: boolean) => void;
  onAspectRatioChange?: (aspectRatio: number | null) => void;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  playerRef,
  src,
  isPlaying,
  loading,
  activeSrcRef,
  isSeeking,
  setLoading,
  setIsReady,
  setPlayedSeconds,
  setDuration,
  setIsPlaying,
  onAspectRatioChange,
}) => {
  return (
    <ReactPlayer
      ref={playerRef}
      src={src}
      width="100%"
      height="100%"
      style={{ objectFit: 'contain' }}
      playing={isPlaying && !loading}
      controls={false}
      playsInline
      crossOrigin="use-credentials"
      onReady={() => {
        if (activeSrcRef.current !== src) return;


        setLoading(false);
        setIsReady(true);
      }}
      onDurationChange={event => {
        setDuration(event.currentTarget.duration || 0);
      }}
      onLoadedMetadata={event => {
        const { videoWidth, videoHeight } = event.currentTarget;

        if (activeSrcRef.current === src && videoWidth > 0 && videoHeight > 0) {
          onAspectRatioChange?.(videoWidth / videoHeight);
        }
      }}
      onTimeUpdate={event => {
        if (!isSeeking) {
          setPlayedSeconds(event.currentTarget.currentTime || 0);
        }
      }}
      onPlay={() => setIsPlaying(true)}
      onPause={() => setIsPlaying(false)}
      onStart={() => {
        if (activeSrcRef.current !== src) return;

        setLoading(false);
      }}
      onError={error => {
        console.error('Video failed', error);

        setLoading(false);
        setIsPlaying(false);
        setIsReady(false);
      }}
    />


  );
};

const ZoomableVideoPlayer = withZoomViewer(VideoPlayer);

const VideoItem: React.FC<VideoItemProps> = memo(
  ({ src, download, downloadFileName, stillFrame, onAspectRatioChange }) => {
    const [loading, setLoading] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [playedSeconds, setPlayedSeconds] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isSeeking, setIsSeeking] = useState(false);


    const playerRef = useRef<any>(null);
    const activeSrcRef = useRef<string | null>(null);

    useEffect(() => {
      activeSrcRef.current = src;

      if (src) {
        setLoading(true);
        setIsPlaying(false);
        setIsReady(false);
        setPlayedSeconds(0);
        setDuration(0);
        setIsSeeking(false);
        onAspectRatioChange?.(null);
      }
    }, [src, onAspectRatioChange]);

    const getInternalPlayer = () => {
      const internal = playerRef.current?.getInternalPlayer?.();

      return internal || playerRef.current;
    };

    // Reload
    const handleReload = useCallback(() => {
      const internalPlayer = getInternalPlayer();

      if (!internalPlayer) return;

      internalPlayer.currentTime = 0;

      setPlayedSeconds(0);

      internalPlayer.play?.().catch?.(() => undefined);

      setIsPlaying(true);
    }, []);

    // Rewind
    const handleRewind = useCallback(() => {
      const internalPlayer = getInternalPlayer();

      if (!internalPlayer) return;

      const nextTime = Math.max(
        0,
        internalPlayer.currentTime - 10
      );

      internalPlayer.currentTime = nextTime;

      setPlayedSeconds(nextTime);

      internalPlayer.play?.().catch?.(() => undefined);

      setIsPlaying(true);
    }, []);

    // Play / Pause
    const handlePlayPause = useCallback(() => {
      const internalPlayer = getInternalPlayer();

      if (internalPlayer) {
        if (isPlaying) {
          internalPlayer.pause?.();
        } else {
          internalPlayer.play?.().catch?.(() => undefined);
        }

        return;
      }

      setIsPlaying(prev => !prev);
    }, [isPlaying]);

    // Forward
    const handleForward = useCallback(() => {
      const internalPlayer = getInternalPlayer();

      if (!internalPlayer) return;

      const duration = internalPlayer.duration || 0;

      const nextTime = Math.min(
        duration,
        internalPlayer.currentTime + 10
      );

      internalPlayer.currentTime = nextTime;

      setPlayedSeconds(nextTime);

      internalPlayer.play?.().catch?.(() => undefined);

      setIsPlaying(true);
    }, []);

    const handleSeekChange = useCallback((value: number) => {
      setIsSeeking(true);

      setPlayedSeconds(value);
    }, []);

    const handleSeekCommit = useCallback((value: number) => {
      playerRef.current?.seekTo?.(value, 'seconds');

      const internalPlayer = getInternalPlayer();

      if (internalPlayer && !playerRef.current?.seekTo) {
        internalPlayer.currentTime = value;
      }

      setPlayedSeconds(value);

      setIsSeeking(false);
    }, []);

    const handleDownload = useCallback(() => {
      if (!src) return;

      download?.(src, downloadFileName || 'video');
    }, [src, download, downloadFileName]);

    const handleStillFrame = useCallback(() => {
      const internalPlayer = getInternalPlayer();

      if (!internalPlayer) return;

      const cTime = Math.max(
        0,
        internalPlayer.currentTime
      );

      if (!src) return;

      // Freeze the captured moment so the modal can switch to the still
      // frame without the video continuing underneath.
      internalPlayer.pause?.();
      setIsPlaying(false);

      // Plain seconds: ffmpeg rejects HH:MM:SS with SS > 59, which the old
      // '00:00:' + totalSeconds format produced for frames past 59s.
      stillFrame?.(
        extractFileName(src),
        cTime.toFixed(3)
      );
    }, [src, stillFrame]);

    const extractFileName = (url: string) => {
      return url.split('/').pop() || '';
    };

    const formatClock = (value: number): string => {
      if (!Number.isFinite(value) || value < 0) {
        return '0:00';
      }

      const totalSeconds = Math.floor(value);

      const hours = Math.floor(totalSeconds / 3600);

      const minutes = Math.floor(
        (totalSeconds % 3600) / 60
      );

      const seconds = totalSeconds % 60;

      if (hours > 0) {
        return `${hours}:${minutes
          .toString()
          .padStart(2, '0')}:${seconds
            .toString()
            .padStart(2, '0')}`;
      }

      return `${minutes}:${seconds
        .toString()
        .padStart(2, '0')}`;
    };

    return (
      <div className="relative w-full h-full bg-black overflow-hidden">
        {src && (
          <ZoomableVideoPlayer
            className="w-full h-full"
            playerRef={playerRef}
            src={src}
            isPlaying={isPlaying}
            loading={loading}
            activeSrcRef={activeSrcRef}
            isSeeking={isSeeking}
            setLoading={setLoading}
            setIsReady={setIsReady}
            setPlayedSeconds={setPlayedSeconds}
            setDuration={setDuration}
            setIsPlaying={setIsPlaying}
            onAspectRatioChange={onAspectRatioChange}
            zoomControls={({
              zoomIn,
              zoomOut,
              resetTransform,
            }) => (
              <VideoControls
                onReload={handleReload}
                onRewind={handleRewind}
                onPlayPause={handlePlayPause}
                onForward={handleForward}
                onDownload={handleDownload}
                onStillFrame={handleStillFrame}
                zoomIn={() => zoomIn()}
                zoomOut={() => zoomOut()}
                resetTransform={() => resetTransform()}
                onSeekChange={handleSeekChange}
                onSeekCommit={handleSeekCommit}
                playedSeconds={playedSeconds}
                duration={duration}
                currentTimeLabel={formatClock(
                  playedSeconds
                )}
                durationLabel={formatClock(duration)}
                isPlaying={isPlaying}
                disabled={!isReady || loading}
              />
            )}
          />
        )}

        {loading && src && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-20">
            <Spin size="large" />
          </div>
        )}
      </div>
    );


  }
);

export default VideoItem;
